import maplibregl from "maplibre-gl";
import { mapStyleReady } from "../../shared/map-style-safe.ts";
import type { LngLat } from "../../shared/types.ts";

function metersToScreenPx(map: maplibregl.Map, meters: number, lat: number): number {
  const y = map.getZoom();
  const mpp = (78271.484 * Math.cos((lat * Math.PI) / 180)) / 2 ** y;
  return Math.max(12, Math.min(240, meters / mpp));
}

/** Blue dot (and optional accuracy ring) for the user's last located position on the home map. */
export class UserLocationMark {
  private readonly map: maplibregl.Map;
  private at: LngLat | null = null;
  private lastAccuracy: number | null = null;
  private dot: maplibregl.Marker | null = null;
  private accuracy: maplibregl.Marker | null = null;

  constructor(map: maplibregl.Map) {
    this.map = map;
  }

  reconcileLayers(): void {
    if (this.at) this.show(this.at, this.lastAccuracy);
  }

  show(at: LngLat, accuracyM: number | null): void {
    this.at = at;
    this.lastAccuracy = accuracyM;
    if (!mapStyleReady(this.map)) {
      this.map.once("idle", () => this.show(at, accuracyM));
      return;
    }
    if (!this.dot) {
      const el = document.createElement("div");
      el.className = "home-user-location-dot";
      el.setAttribute("aria-hidden", "true");
      this.dot = new maplibregl.Marker({ element: el, anchor: "center" });
    }
    this.dot.setLngLat(at);
    if (!this.dot.getElement().isConnected) this.dot.addTo(this.map);

    if (accuracyM != null && Number.isFinite(accuracyM) && accuracyM > 8) {
      if (!this.accuracy) {
        const ring = document.createElement("div");
        ring.className = "home-user-location-accuracy";
        ring.setAttribute("aria-hidden", "true");
        this.accuracy = new maplibregl.Marker({ element: ring, anchor: "center" });
      }
      const px = metersToScreenPx(this.map, accuracyM, at[1]);
      const ringEl = this.accuracy.getElement();
      ringEl.style.width = `${px}px`;
      ringEl.style.height = `${px}px`;
      this.accuracy.setLngLat(at);
      if (!ringEl.isConnected) this.accuracy.addTo(this.map);
    } else {
      this.accuracy?.remove();
      this.accuracy = null;
    }
  }

  clear(): void {
    this.at = null;
    this.lastAccuracy = null;
    this.dot?.remove();
    this.dot = null;
    this.accuracy?.remove();
    this.accuracy = null;
  }
}
